import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col } from 'react-flexbox-grid';
import Time from 'react-time';
import { FormattedMessage } from 'react-intl';
import { map, filter, isError, get } from 'lodash';
import RaisedButton from 'material-ui/RaisedButton';

import { fromNowLocale } from 'utils/dateTools';
import { shouldNotHide, shouldShow, shouldEdit } from 'utils/valuesByKey';
import { getGender } from 'utils/genders';
import KTabs from 'components/KJRN/KTabs';
import { Divider } from 'material-ui';
import KTab from 'components/KJRN/KTab';
import KHero from 'components/KJRN/KHero';
import KFlatButton from 'components/KJRN/KFlatButton';
import { fetchProfileIfNeeded } from '../actions/profile';
import PanelMessage from '../components/panels/PanelMessage';
import Loading from '../components/Loading';
import EmployeeCompanyConnectionsTabs from '../components/profile/EmployeeCompanyConnectionsTabs';
import EmployeeEditForm from '../components/profile/EmployeeEditForm';
import EmployeeBottonTabs from '../components/profile/EmployeeBottonTabs';
import DetailList from '../components/DetailList';

const flexJustify = {
  display: 'flex', justifyContent: 'center', width: '100%', marginBottom: 24,
};

const fix = { overflow: 'visible' }; // fix material-ui https://my.origo.is/browse/APPAIL-7744

const Profile = () => {
  const dispatch = useDispatch();
  const { profileReducer, WebValuesReducer, languageReducer } = useSelector(state => state);
  const {
    masterError, detailError, employeeMaster, employeeMasterLoaded, employeeDetailOrgListLoaded,
  } = profileReducer;
  const { valuesByKey } = WebValuesReducer;
  const { language } = languageReducer;

  useEffect(() => {
    dispatch(fetchProfileIfNeeded());
  }, []);

  if (!(employeeMasterLoaded && employeeDetailOrgListLoaded)) {
    return <Loading />;
  }

  if (isError(masterError) || isError(detailError)) {
    return (
      <Col xs={12} sm={12} md={6} mdOffset={3}>
        <PanelMessage
          type="error"
          iconName="error"
          message={<FormattedMessage id="profile.relog" />}
        />
      </Col>
    );
  }

  let controller;
  const editProfile = () => controller.editProfile();
  const editFamily = () => controller.editFamily();
  const editRelative = () => controller.editRelative();

  const relativeAssociationOptions = {
    0: <FormattedMessage id="profile.relativeAssociation.unknown" />,
    1: <FormattedMessage id="profile.relativeAssociation.spouse" />,
    2: <FormattedMessage id="profile.relativeAssociation.parent" />,
    3: <FormattedMessage id="profile.relativeAssociation.sibling" />,
    4: <FormattedMessage id="profile.relativeAssociation.relative" />,
    5: <FormattedMessage id="profile.relativeAssociation.other" />,
  };
  const {
    Address, LegalAddress, LegalPostCode, LegalPostCodeName, PostCode, PostCodeName,
    RelativeName, RelativePhoneNumber, RecrutingDateYearMonthDays, RecrutingDatePayYearMonthDays,
    RelativeName2, RelativePhoneNumber2, RelativeAssociation, RelativeAssociation2, RelativePhoneNumberMobile, RelativePhoneNumberMobile2,
    RelativeEmail, RelativeEmail2, RelativeAddress, RelativeAddress2,
  } = employeeMaster;

  const detailData = [
    {
      label: <div style={fix} data-cy="EmployeeMaster.EntityNR"><FormattedMessage id="profile.ssn" /></div>,
      value: employeeMaster.EntityNR,
      visible: shouldNotHide(valuesByKey, 'EmployeeMaster.EntityNR'),
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.age" /></div>,
      value: employeeMaster.Age,
      visible: shouldNotHide(valuesByKey, 'EmployeeMaster.Age'),
    },
    {
      label: <div style={fix} data-cy="EmployeeMaster.GenderTypeName"><FormattedMessage id="profile.gender" /></div>,
      value: getGender(language, employeeMaster.GenderType),
      visible: shouldNotHide(valuesByKey, 'EmployeeMaster.GenderTypeName'),
    },
    {
      label: <div style={fix} data-cy="EmployeeMaster.LastHireDate"><FormattedMessage id="profile.lastHire" /></div>,
      value: <Time value={employeeMaster.LastHireDate || new Date()} format="DD.MM.YYYY" />,
      visible: shouldNotHide(valuesByKey, 'EmployeeMaster.LastHireDate'),
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.recruitingDate" /></div>,
      value: fromNowLocale(language, RecrutingDateYearMonthDays),
      visible: shouldNotHide(valuesByKey, 'EmployeeMaster.RecrutingDate'),
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.recruitingDatePay" /></div>,
      value: fromNowLocale(language, RecrutingDatePayYearMonthDays),
      visible: shouldNotHide(valuesByKey, 'EmployeeMaster.RecrutingDatePay'),
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.legalAddress" /></div>,
      value: [LegalAddress, LegalPostCode, LegalPostCodeName].filter(x => x).join(', '),
      visible: shouldNotHide(valuesByKey, 'EmployeeMaster.LegalAddress'),
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.address" /></div>,
      value: [Address, PostCode, PostCodeName].filter(x => x).join(', '),
      visible: shouldNotHide(valuesByKey, 'EmployeeMaster.Address'),
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.username" /></div>,
      value: employeeMaster.UserName,
      visible: shouldNotHide(valuesByKey, 'EmployeeMaster.UserName'),
    },
    {
      label: <div style={fix} data-cy="EmployeeMaster.EmployeeMasterID"><FormattedMessage id="profile.employeeMasterID" /></div>,
      value: employeeMaster.EmployeeMasterID,
      visible: shouldNotHide(valuesByKey, 'EmployeeMaster.EmployeeMasterID'),
    },
    {
      label: <div style={fix} data-cy="EmployeeMaster.PhoneNumberBuisness"><FormattedMessage id="profile.workPhone" /></div>,
      value: employeeMaster.PhoneNumberBuisness,
      visible: shouldNotHide(valuesByKey, 'EmployeeMaster.PhoneNumberBuisness'),
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.mobile" /></div>,
      value: `${employeeMaster.PhoneNumberMobileCountryCode || ''} ${employeeMaster.PhoneNumberMobile || ''}`,
      visible: shouldNotHide(valuesByKey, 'EmployeeMaster.PhoneNumberMobile'),
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.tetra" /></div>,
      value: employeeMaster.Tetra,
      visible: shouldNotHide(valuesByKey, 'EmployeeMaster.Tetra'),
    },
    {
      label: <div style={fix} data-cy="EmployeeMaster.PhoneNumberHome"><FormattedMessage id="profile.phone" /></div>,
      value: employeeMaster.PhoneNumberHome,
      visible: shouldNotHide(valuesByKey, 'EmployeeMaster.PhoneNumberHome'),
    },
    {
      label: <div style={fix} data-cy="EmployeeMaster.EmailWork"><FormattedMessage id="profile.workEmail" /></div>,
      value: employeeMaster.EmailWork,
      visible: shouldNotHide(valuesByKey, 'EmployeeMaster.EmailWork'),
    },
    {
      label: <div style={fix} data-cy="EmployeeMaster.Email"><FormattedMessage id="profile.email" /></div>,
      value: employeeMaster.Email,
      visible: shouldNotHide(valuesByKey, 'EmployeeMaster.Email'),
    },
    {
      label: <div style={fix}><FormattedMessage id="profile.accessCard" /></div>,
      value: employeeMaster.IdentificationCard,
      visible: shouldShow(valuesByKey, 'EmployeeDetail.IdentificationCard'),
    },
  ];

  const filteredData = filter(detailData, x => x.visible);

  const children = map(employeeMaster.EmployeeMasterChildren, c => ({
    label: <div style={fix}><FormattedMessage id="profile.child.name" /></div>, value: `${c.ChildName}, (${c.Birthyear})`,
  }));
  const relative1 = [
    { label: <div style={fix}><FormattedMessage id="profile.relativeName" /></div>, value: RelativeName, visible: !!RelativeName },
    { label: <div style={fix}><FormattedMessage id="profile.relativePhone" /></div>, value: RelativePhoneNumber, visible: !!RelativePhoneNumber },
    { label: <div style={fix}><FormattedMessage id="profile.relativePhoneMobile" /></div>, value: RelativePhoneNumberMobile, visible: !!RelativePhoneNumberMobile },
    { label: <div style={fix}><FormattedMessage id="profile.relativeRelation" /></div>, value: get(relativeAssociationOptions, RelativeAssociation), visible: !!RelativeAssociation },
    { label: <div style={fix}><FormattedMessage id="profile.relativeEmail" /></div>, value: RelativeEmail, visible: !!RelativeEmail },
    { label: <div style={fix}><FormattedMessage id="profile.relativeAddress" /></div>, value: RelativeAddress, visible: !!RelativeAddress },
  ];
  const relative2 = [
    { label: <div style={fix}><FormattedMessage id="profile.relativeName" /></div>, value: RelativeName2, visible: !!RelativeName2 },
    { label: <div style={fix}><FormattedMessage id="profile.relativePhone" /></div>, value: RelativePhoneNumber2, visible: !!RelativePhoneNumber2 },
    { label: <div style={fix}><FormattedMessage id="profile.relativePhoneMobile" /></div>, value: RelativePhoneNumberMobile2, visible: !!RelativePhoneNumberMobile2 },
    { label: <div style={fix}><FormattedMessage id="profile.relativeRelation" /></div>, value: get(relativeAssociationOptions, RelativeAssociation2), visible: !!RelativeAssociation2 },
    { label: <div style={fix}><FormattedMessage id="profile.relativeEmail" /></div>, value: RelativeEmail2, visible: !!RelativeEmail2 },
    { label: <div style={fix}><FormattedMessage id="profile.relativeAddress" /></div>, value: RelativeAddress2, visible: !!RelativeAddress2 },
  ];
  return (
    <React.Fragment>
      <Row style={{ margin: 0 }} className="no-print">
        <EmployeeEditForm
          dispatch={dispatch}
          controller={(ctrl) => { controller = ctrl; }}
        />
        <Col xs={12} lgOffset={1} lg={10}>
          <KHero
            title={employeeMaster.EmployeeName}
            imgSrc={employeeMaster.ImageUrl}
            masterId={employeeMaster.EmployeeMasterID}
          />
        </Col>
        <Col style={flexJustify} xs={12} sm={12} md={12} lg={6} lgOffset={1}>
          <KTabs>
            <KTab style={{ padding: 12 }} name={<FormattedMessage id="profile.employeeMaster" />}>
              <DetailList
                containerStyle={{
                  flexFlow: 'row wrap',
                  justifyContent: 'space-between',
                }}
                itemMaxWidth="50%"
                dataSource={filteredData}
              />
              {shouldEdit(valuesByKey, 'EmployeeMaster') &&
                <KFlatButton
                  iconName="edit"
                  containerStyle={{ justifyContent: 'flex-end' }}
                  label={<FormattedMessage id="profile.editProfile" />}
                  onClick={editProfile}
                />
              }
            </KTab>
            {shouldNotHide(valuesByKey, 'EmployeeMasterChild') &&
              <KTab style={{ padding: 12 }} name={<FormattedMessage id="profile.family" />}>
                <DetailList dataSource={children} />
                <KFlatButton
                  iconName="edit"
                  onClick={editFamily}
                  containerStyle={{ justifyContent: 'flex-end' }}
                  label={<FormattedMessage id="profile.editProfile" />}
                />
              </KTab>
            }{shouldNotHide(valuesByKey, 'EmployeeMaster.Relative') &&
              <KTab style={{ padding: 12 }} name={<FormattedMessage id="profile.relative" />}>
                <div style={{ marginTop: 12, fontSize: 14, fontWeight: 600 }}>
                  <FormattedMessage id="profile.relative" />
                </div>
                <DetailList dataSource={relative1} />
                <Divider style={{ margin: 16 }} />
                <div style={{ marginTop: 12, fontSize: 14, fontWeight: 600 }}>
                  <FormattedMessage id="profile.relative2" />
                </div>
                <DetailList dataSource={relative2} />
                <KFlatButton
                  iconName="edit"
                  onClick={editRelative}
                  containerStyle={{ justifyContent: 'flex-end' }}
                  label={<FormattedMessage id="profile.editProfile" />}
                />
              </KTab>
            }
          </KTabs>
        </Col>
        <Col style={flexJustify} xs={12} sm={12} md={12} lg={4}>
          <EmployeeCompanyConnectionsTabs />
        </Col>
      </Row>
      <Row style={{ margin: 0 }}>
        <Col xs={12} sm={12} md={12} lg={10} lgOffset={1} style={{ ...flexJustify, position: 'relative' }}>
          <RaisedButton
            style={{ position: 'absolute', right: 16, top: 12 }}
            label={<FormattedMessage id="print" />}
            primary
            onClick={() => window.print()}
            className="no-print"
          />
          <EmployeeBottonTabs />
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default Profile;



// WEBPACK FOOTER //
// ./src/containers/Profile.js